<template>
  <section class="bg-gray-50 dark:bg-gray-900">
    <div class="flex flex-col items-center justify-center px-6 py-8 mx-auto h-screen lg:py-0">
      <Link href="/" class="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white">
        <img class="w-8 h-8 mr-2" src="https://flowbite.s3.amazonaws.com/blocks/marketing-ui/logo.svg" alt="logo">
        {{ $t('title') }}
      </Link>

      <!-- alert -->
      <div class="mb-4 w-full sm:max-w-md">
        <FlashNotice></FlashNotice>
        <FlashAlert :message="errors"></FlashAlert>
      </div>

      <div class="relative w-full bg-white border-t-4 border-blue-500 rounded-b-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700">
        <div class="p-6 space-y-4 md:space-y-6 sm:p-8">
          <template v-if="googleLogin">
            <!-- with google oauth login -->
            <h1 v-t="'sessions.new.title'" class="text-xl text-center leading-tight tracking-tight text-gray-400 dark:text-white"></h1>

            <a href="/users/auth/google_oauth2" data-method="post" class="btn btn-primary w-full">
              <i class="fab fa-google me-2"></i>
              {{ $t('sessions.new.google_sign_in') }}
            </a>
          </template>
          <template v-else>
            <!-- with password -->
            <h1 v-t="'sessions.new.title'" class="text-xl font-bold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white"></h1>

            <form @submit.prevent="onSubmit" class="space-y-4 md:space-y-6" action="#">
              <!-- email -->
              <div>
                <label v-t="'activerecord.attributes.user.email'" for="email" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"></label>
                <input v-model="form.user.email" type="email" autocomplete="off" autofocus name="email" id="email" class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" :placeholder="$t('activerecord.placeholders.user.email')" required="">
              </div>

              <!-- password -->
              <div>
                <label v-t="'activerecord.attributes.user.password'" for="password" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"></label>
                <input v-model="form.user.password" type="password" name="password" id="password" :placeholder="$t('activerecord.placeholders.user.password')" class="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" required="">

                <!-- <div class="flex justify-end mt-2">
                  <Link v-t="'sessions.new.forgot_password'" href="/users/password/new" class="label-text-alt text-sm font-medium text-primary-600 hover:underline"></Link>
                </div> -->
              </div>

              <button type="submit" class="btn btn-primary w-full">
                <span v-if="form.processing" class="me-1">
                  <svg aria-hidden="true" class="w-3 h-3 -mt-1 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600 inline-block" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/></svg>
                </span>
                {{ $t('sign_in') }}
              </button>
            </form>
          </template>
        </div>
      </div>

      <div class="inline-flex justify-end mt-1 me-4 w-full sm:max-w-md">
        <span @click="toggleLogin" class="text-xs cursor-pointer font-mono text-gray-500">
          <template v-if="googleLogin">
            {{ $t('sessions.new.with_password') }}
          </template>
          <template v-else>
            {{ $t('sessions.new.with_google') }}
          </template>
        </span>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useForm } from '@inertiajs/vue3'
import Rails from '@rails/ujs'

defineProps({ errors: String })

const defaultForm = {
  user: {
    email: undefined, password: undefined
  }
}
const form = useForm(defaultForm)
const googleLogin = ref(true)

const toggleLogin = () => {
  googleLogin.value = !googleLogin.value
  form.reset()
}

const onSubmit = () => {
  const url = '/users/sign_in'
  form.post(url)
}

onMounted(() => {
  try {
    Rails.start()
  } catch (e) {}
})
</script>
